import React from 'react';
import PropTypes from 'prop-types';
import {Editor} from 'react-draft-wysiwyg';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';
import {EditorState, convertToRaw, ContentState} from 'draft-js';
import htmlToDraft from 'html-to-draftjs';
import draftToHtml from 'draftjs-to-html';

import {getMessage as msg} from 'MessageBundle'
import {validate, generateStrategyResult, required, focusFirstError} from 'validation';
import Popup from 'common/Popup';
import Input from 'common/Input';
import ErrorBox from 'common/ErrorBox'
import BaseComponent from 'common/BaseComponent';
import theme from './EmailPopup.scss'

const toolbar = {
    options: ['inline', 'blockType', 'fontSize', 'fontFamily', 'list', 'textAlign', 'link', 'history']
};

const wrapperStyle = {
    width: 692
};

const editorStyle = {
    width: 692,
    height: 360
};

export default class EmailPopup extends BaseComponent {

    constructor(props) {
        super(props);

        this.state = this.getData(props);
        this.state.errorMessages = {};

        const requiredHtml = (errorMessage) => ([property]) => {
            let errors = [];
            const value = this.convertToHtml(property);
            if (value === "<p></p>\n") {
                errors = [errorMessage];
            }
            return generateStrategyResult(errors);
        };

        this.validation = this.getValidation(requiredHtml);
        this.elementOrder = {
            subject : "subject",
            editorState : () => document.getElementsByClassName("public-DraftEditor-content")[0]
        };
    }

    getValidation = (requiredHtml) => {
        if (this.props.emailDocument){
            return {
                save: [
                    {
                        properties: ["state.content"],
                        individualNames: ["editorState"],
                        strategies: [
                            requiredHtml(msg("emailPopup.validation.documentContentRequired"))
                        ]
                    },
                    {
                        properties: ["state.subject"],
                        individualNames: ["subject"],
                        strategies: [
                            required(msg("emailPopup.validation.subjectRequired"))
                        ]
                    }
                ]
            };
        } else {
            return {
                save: [
                    {
                        properties: ["state.content"],
                        individualNames: ["editorState"],
                        strategies: [
                            requiredHtml(msg("emailPopup.validation.templateContentRequired"))
                        ]
                    }
                ]
            };
        }
    };

    componentDidMount() {
        this.subject.focus();
    }

    componentWillReceiveProps(nextProps){
        this.setState(this.getData(nextProps));
    }

    getData = (props) => {
        const contentBlock = htmlToDraft(props.value.content);
        let editorState;
        if (contentBlock) {
            const contentState = ContentState.createFromBlockArray(contentBlock.contentBlocks);
            editorState = EditorState.createWithContent(contentState);
        } else {
            editorState = EditorState.createEmpty();
        }
        return {
            subject: props.value.subject,
            content: editorState
        };
    };

    handleTemplateClose = () => {
        this.props.onClose();

        this.setState({
            errorMessages: {}
        });
    };

    handleTemplateSave = () => {
        const validationResult = validate(this, this.validation, ["save"]);
        if (validationResult.isEmpty()) {
            this.props.onSave(this.state.subject, this.convertToHtml(this.state.content));

            this.setState({
                errorMessages: {}
            });
        } else {
            let newErrorMessages = validationResult.getErrorMessages();
            this.setState({
                errorMessages: newErrorMessages
            });
            focusFirstError(this.elementOrder, newErrorMessages);
        }
    };

    convertToHtml = (editor) => {
        return draftToHtml(convertToRaw(editor.getCurrentContent()));
    };

    renderButtonGroup = (readonly) => {
        if (readonly) {
            return (
                <div className={theme.btnGroup}>
                    <button onClick={this.handleTemplateClose} className={theme.buttonAccent}>{msg('emailPopup.createEmail.closeButton')}</button>
                </div>
            )
        } else {
            return (
                <div className={theme.btnGroup}>
                    <button onClick={this.handleTemplateClose}>{msg('emailPopup.createEmail.cancelButton')}</button>
                    <button onClick={this.handleTemplateSave} className={theme.buttonAccent}>{msg('emailPopup.createEmail.saveButton')}</button>
                </div>
            )
        }
    };

    getEmailPopupHeader = (document, readonly) => {
        const documentHeader = {
            view: "emailPopup.header.document.view",
            edit: "emailPopup.header.document.edit"
        };

        const templateHeader = {
            view: "emailPopup.header.template.view",
            edit: "emailPopup.header.template.edit"
        };

        const header = document ? documentHeader : templateHeader;
        return readonly ? header.view : header.edit;
    };

    render() {
        const readonly = this.props.value.readonly;
        const document = this.props.emailDocument;

        return (
            <Popup visible={true}>
                <div className={theme.popupContent}>
                    <h2>{msg(this.getEmailPopupHeader(document, readonly))}</h2>
                    <div className={readonly ? theme.popupReadonly : ""}>
                        <ErrorBox groups={["save"]} errorMessages={this.state.errorMessages}/>
                        <Input id="subject" value={this.state.subject} name="subject" errorMessages={this.state.errorMessages}
                            readOnly={readonly} onChange={this.handleChange('subject')}
                            required={this.props.emailDocument} ref={(subject) => this.subject = subject}
                            label={msg('emailPopup.createEmail.subjectLabel')}  className={theme.row}/>
                        <Editor name="content" toolbar={toolbar} editorState={this.state.content} readOnly={readonly} tabIndex="0"
                            onEditorStateChange={this.handleChange('content')} editorStyle={editorStyle} wrapperStyle={wrapperStyle}/>
                        {this.renderButtonGroup(readonly)}
                    </div>
                </div>
            </Popup>
        );
    }
}

EmailPopup.propTypes = {


    /**
     * Data content to be shown or edited.
     */
    value: PropTypes.object.isRequired,

    /**
     * JavaScript function to be executed on close button click.
     */
    onClose: PropTypes.func.isRequired,

    /**
     * JavaScript function to be exected on save button click.
     */
    onSave: PropTypes.func.isRequired,

    /**
     * Boolean flag indicating that popup is email document.
     */
    emailDocument: PropTypes.bool
};


// WEBPACK FOOTER //
// ./src/common/EmailPopup/index.js